/**
 * Client-side Snags & variations → PDF (jsPDF).
 * Tailored for snag title, status, priority, category, teams, description, media, replies and PM notes.
 */

import { jsPDF } from 'jspdf';
import { loadLogoDataUrlForPdf } from './pdfCompanyLogo';
import {
  fitImageDrawSize,
  PDF_PRIMARY_PHOTO_BOX,
  PDF_INLINE_PHOTO_BOX,
  PDF_PM_NOTE_PHOTO_BOX,
} from './pdfImageFit';

const PT_H = 842;
const MARGIN = 40;
const CONTENT_W = 515;
const MAX_IMG_W_PT = 500;
const BOTTOM = 56;
const INDENT = 16;
const GRID_COL_W = (CONTENT_W - 8) / 2;
const GRID_IMG_BOX = { maxW: GRID_COL_W, maxH: 200 };
const VOICE_PDF = 'Voice note: not included in this PDF — use the app to listen.';

/**
 * @param {string} url
 * @returns {Promise<{ dataUrl: string; wPt: number; hPt: number } | null>}
 */
async function loadImageForPdf(url) {
  if (!url || typeof url !== 'string') return null;
  return new Promise((resolve) => {
    const go = (src, cleanup) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        cleanup?.();
        const canvas = document.createElement('canvas');
        const maxPx = 1200;
        let w = img.naturalWidth || img.width;
        let h = img.naturalHeight || img.height;
        if (!w || !h) { resolve(null); return; }
        const scale = Math.min(1, maxPx / Math.max(w, h));
        w = Math.round(w * scale);
        h = Math.round(h * scale);
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        try { ctx.drawImage(img, 0, 0, w, h); } catch { resolve(null); return; }
        let dataUrl;
        try { dataUrl = canvas.toDataURL('image/jpeg', 0.88); } catch { resolve(null); return; }
        const wPt = Math.min(MAX_IMG_W_PT, w * 0.75);
        const hPt = (h / w) * wPt;
        resolve({ dataUrl, wPt, hPt });
      };
      img.onerror = () => { cleanup?.(); resolve(null); };
      img.src = src;
    };

    if (url.startsWith('data:')) { go(url, null); return; }
    fetch(url, { mode: 'cors' })
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        go(objectUrl, () => URL.revokeObjectURL(objectUrl));
      })
      .catch(() => go(url, null));
  });
}

function stampFooter(pdf, n, projectName, { startPage = 1, pageOffset = 0 } = {}) {
  for (let i = startPage; i <= n; i += 1) {
    pdf.setPage(i);
    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.4);
    pdf.line(MARGIN, PT_H - 38, MARGIN + CONTENT_W, PT_H - 38);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(160, 160, 160);
    pdf.text(projectName || 'CrewChain', MARGIN, PT_H - 26);
    const displayPage = i - pageOffset;
    const totalPages = n - pageOffset;
    pdf.text(`Page ${displayPage} of ${totalPages}`, MARGIN + CONTENT_W, PT_H - 26, { align: 'right' });
    pdf.setLineWidth(0.2);
  }
}

/**
 * @param {object} options
 * @param {string} options.projectName
 * @param {string} [options.filterSummary]
 * @param {Array<{
 *   title: string,
 *   statusLabel: string,
 *   priorityLabel: string,
 *   categoryLabel: string,
 *   createdLine: string,
 *   assignedLine: string | null,
 *   resolvedLine: string | null,
 *   description: string,
 *   snagVoiceLine: string | null,
 *   imageUrls: string[],
 *   replies: Array<{
 *     name: string,
 *     timeLabel: string,
 *     text: string,
 *     imageUrls: string[],
 *     voiceLine: string | null,
 *     pmNotes: Array<{ name: string, timeLabel: string, text: string, imageUrls: string[], voiceLine: string | null }>
 *   }>
 * }>} options.items — oldest (or chosen order) first
 * @param {string} [options.companyLogoUrl] — optional; top-right on page 1 when set
 */
export async function buildSnagsExportPdf({ projectName, filterSummary, items, companyLogoUrl = null }) {
  const logo = companyLogoUrl ? await loadLogoDataUrlForPdf(companyLogoUrl) : null;

  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageH = PT_H;
  let y = MARGIN;

  const addPage = () => { pdf.addPage(); y = MARGIN; };
  const addPageIfNeeded = (needPt) => { if (y + needPt > pageH - BOTTOM) addPage(); };

  const writeLines = (text, size = 10, bold = false) => {
    if (!text) return;
    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(text, CONTENT_W);
    for (const line of lines) { addPageIfNeeded(14); pdf.text(line, MARGIN, y); y += size + 2; }
  };

  const gen = new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

  // ── Cover page ──────────────────────────────────────────────────────────────
  if (logo) {
    const logoX = 595 - MARGIN - logo.wPt;
    try { pdf.addImage(logo.dataUrl, logo.format, logoX, 20, logo.wPt, logo.hPt, undefined, 'FAST'); } catch { /* */ }
  }

  let coverY = logo ? Math.max(140, 28 + logo.hPt + 20) : 140;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(30);
  pdf.setTextColor(30, 41, 59);
  const coverPnLines = pdf.splitTextToSize(projectName || 'Project', CONTENT_W);
  for (const line of coverPnLines) { pdf.text(line, MARGIN, coverY); coverY += 36; }

  coverY += 4;
  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.5);
  pdf.line(MARGIN, coverY, MARGIN + CONTENT_W, coverY);
  coverY += 16;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(15);
  pdf.setTextColor(100, 116, 139);
  pdf.text('Snags & Variations Export', MARGIN, coverY);
  coverY += 28;

  pdf.setFontSize(9);
  pdf.setTextColor(148, 163, 184);
  pdf.text(`Generated: ${gen}`, MARGIN, coverY); coverY += 24;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(71, 85, 105);
  let countLabel;
  if (filterSummary) {
    countLabel = filterSummary;
  } else {
    const nSnags = items.filter(it => it.category !== 'variation').length;
    const nVars = items.filter(it => it.category === 'variation').length;
    if (nSnags > 0 && nVars > 0) countLabel = `${nSnags} snag${nSnags !== 1 ? 's' : ''} · ${nVars} variation${nVars !== 1 ? 's' : ''}`;
    else if (nVars > 0) countLabel = `${nVars} variation${nVars !== 1 ? 's' : ''}`;
    else countLabel = `${nSnags} snag${nSnags !== 1 ? 's' : ''}`;
  }
  pdf.text(countLabel, MARGIN, coverY); coverY += 36;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(148, 163, 184);
  pdf.text('Generated by CrewChain', MARGIN, PT_H - 26);

  // ── Content pages ────────────────────────────────────────────────────────────
  pdf.addPage();
  y = MARGIN;

  let snagIndex = 0;

  for (const it of items) {
    snagIndex += 1;
    addPageIfNeeded(100);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    const itemTypeLabel = it.category === 'variation' ? 'Variation' : 'Snag';
    pdf.text(`${itemTypeLabel} ${snagIndex}`, MARGIN, y); y += 16;
    pdf.setFontSize(13);
    const snagTitleLines = pdf.splitTextToSize(it.title || 'Untitled', CONTENT_W);
    for (const line of snagTitleLines) { addPageIfNeeded(16); pdf.text(line, MARGIN, y); y += 15; }
    y += 4;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    const badges = [it.statusLabel, it.priorityLabel, it.categoryLabel].filter(Boolean).join('  ·  ');
    if (badges) {
      const bl = pdf.splitTextToSize(badges, CONTENT_W);
      for (const line of bl) { addPageIfNeeded(13); pdf.text(line, MARGIN, y); y += 12; }
    }
    if (it.createdLine) writeLines(it.createdLine, 10, false);
    if (it.assignedLine) { pdf.setTextColor(60, 60, 60); writeLines(it.assignedLine, 9, false); pdf.setTextColor(0, 0, 0); }
    if (it.resolvedLine) { pdf.setTextColor(16, 185, 129); writeLines(it.resolvedLine, 9, false); pdf.setTextColor(0, 0, 0); }
    y += 4;

    if (it.description) writeLines(it.description, 10, false);
    if (it.snagVoiceLine) {
      pdf.setFontSize(9);
      const vl = pdf.splitTextToSize(it.snagVoiceLine, CONTENT_W);
      for (const line of vl) { addPageIfNeeded(11); pdf.text(line, MARGIN, y); y += 10; }
      pdf.setFontSize(10);
    }

    // 2-up image grid
    const urls = it.imageUrls || [];
    if (urls.length > 0) addPageIfNeeded(160);
    for (let imgIdx = 0; imgIdx < urls.length; imgIdx += 2) {
      const isDouble = imgIdx + 1 < urls.length;
      if (isDouble) {
        const [raw1, raw2] = await Promise.all([
          loadImageForPdf(urls[imgIdx]),
          loadImageForPdf(urls[imgIdx + 1]),
        ]);
        const img1 = raw1 ? fitImageDrawSize(raw1, GRID_IMG_BOX) : null;
        const img2 = raw2 ? fitImageDrawSize(raw2, GRID_IMG_BOX) : null;
        const rowH = Math.max(img1?.hPt ?? 0, img2?.hPt ?? 0);
        if (rowH > 0) {
          if (y + rowH + 12 > pageH - BOTTOM) addPage();
          if (img1) { try { pdf.addImage(img1.dataUrl, 'JPEG', MARGIN, y, img1.wPt, img1.hPt, undefined, 'FAST'); } catch { /* */ } }
          if (img2) { try { pdf.addImage(img2.dataUrl, 'JPEG', MARGIN + GRID_COL_W + 8, y, img2.wPt, img2.hPt, undefined, 'FAST'); } catch { /* */ } }
          y += rowH + 8;
        } else {
          addPageIfNeeded(12);
          pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
          pdf.text('[Images could not be embedded]', MARGIN, y);
          pdf.setTextColor(0, 0, 0); pdf.setFontSize(10); y += 12;
        }
      } else {
        const raw = await loadImageForPdf(urls[imgIdx]);
        const loaded = raw ? fitImageDrawSize(raw, PDF_PRIMARY_PHOTO_BOX) : null;
        if (!loaded) {
          addPageIfNeeded(12);
          pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
          pdf.text('[Image could not be embedded]', MARGIN, y);
          pdf.setTextColor(0, 0, 0); pdf.setFontSize(10); y += 12;
          continue;
        }
        if (y + loaded.hPt + 12 > pageH - BOTTOM) addPage();
        try {
          pdf.addImage(loaded.dataUrl, 'JPEG', MARGIN, y, loaded.wPt, loaded.hPt, undefined, 'FAST');
          y += loaded.hPt + 8;
        } catch {
          addPageIfNeeded(12);
          pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
          pdf.text('[Image could not be embedded]', MARGIN, y);
          pdf.setTextColor(0, 0, 0); pdf.setFontSize(10); y += 12;
        }
      }
    }

    if (it.replies && it.replies.length > 0) {
      addPageIfNeeded(28);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text('Replies (site)', MARGIN, y); y += 14;
      for (const rep of it.replies) {
        const label = rep.name
          ? `Reply — ${rep.name} · ${rep.timeLabel}`
          : `Reply · ${rep.timeLabel}`;
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
        const labLines = pdf.splitTextToSize(label, CONTENT_W);
        for (const line of labLines) { addPageIfNeeded(12); pdf.text(line, MARGIN, y); y += 11; }
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
        if (rep.text) {
          const tl = pdf.splitTextToSize(String(rep.text), CONTENT_W);
          for (const line of tl) { addPageIfNeeded(12); pdf.text(line, MARGIN, y); y += 11; }
        }
        if (rep.voiceLine) {
          const vl2 = pdf.splitTextToSize(rep.voiceLine, CONTENT_W);
          for (const line of vl2) { addPageIfNeeded(10); pdf.setFontSize(8); pdf.text(line, MARGIN, y); y += 10; }
          pdf.setFontSize(10);
        }
        for (const cu of rep.imageUrls || []) {
          const raw = await loadImageForPdf(cu);
          const cImg = raw ? fitImageDrawSize(raw, PDF_INLINE_PHOTO_BOX) : null;
          if (cImg && cImg.wPt > 0) {
            if (y + cImg.hPt > pageH - BOTTOM) addPage();
            try { pdf.addImage(cImg.dataUrl, 'JPEG', MARGIN, y, cImg.wPt, cImg.hPt); y += cImg.hPt + 6; } catch { /* */ }
          }
        }

        for (const pm of rep.pmNotes || []) {
          y += 4;
          const pmLabel = pm.name
            ? `PM note — ${pm.name} · ${pm.timeLabel}`
            : `PM note · ${pm.timeLabel}`;
          pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
          const pmLab = pdf.splitTextToSize(pmLabel, CONTENT_W - INDENT);
          for (const line of pmLab) { addPageIfNeeded(12); pdf.text(line, MARGIN + INDENT, y); y += 11; }
          pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
          if (pm.text) {
            const pml = pdf.splitTextToSize(String(pm.text), CONTENT_W - INDENT);
            for (const line of pml) { addPageIfNeeded(12); pdf.text(line, MARGIN + INDENT, y); y += 11; }
          }
          if (pm.voiceLine) {
            const pvl = pdf.splitTextToSize(pm.voiceLine, CONTENT_W - INDENT);
            for (const line of pvl) { addPageIfNeeded(10); pdf.setFontSize(8); pdf.text(line, MARGIN + INDENT, y); y += 10; }
            pdf.setFontSize(10);
          }
          for (const pu of pm.imageUrls || []) {
            const raw = await loadImageForPdf(pu);
            const pImg = raw ? fitImageDrawSize(raw, PDF_PM_NOTE_PHOTO_BOX) : null;
            if (pImg && pImg.wPt > 0) {
              if (y + pImg.hPt > pageH - BOTTOM) addPage();
              try { pdf.addImage(pImg.dataUrl, 'JPEG', MARGIN + INDENT, y, pImg.wPt, pImg.hPt); y += pImg.hPt + 6; } catch { /* */ }
            }
          }
        }
        y += 6;
      }
    }

    y += 8;
    addPageIfNeeded(20);
    pdf.setDrawColor(51, 65, 85);
    pdf.setLineWidth(0.75);
    pdf.line(MARGIN, y, MARGIN + CONTENT_W, y);
    pdf.setLineWidth(0.2);
    y += 16;
  }

  stampFooter(pdf, pdf.getNumberOfPages(), projectName, { startPage: 2, pageOffset: 1 });

  const safe = (projectName || 'project').replace(/[^a-z0-9]/gi, '-');
  const d = new Date();
  const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  pdf.save(`snags-export-${safe}-${stamp}.pdf`);
}

/**
 * Sections-based snag report: groups of snags with PM annotation headers.
 * Renders a cover page first, then section content with running footers.
 * @param {object} options
 * @param {string} options.projectName
 * @param {Array<{ name: string, annotation: string, items: Array }>} options.sections
 * @param {string} [options.companyLogoUrl]
 */
export async function buildSnagsSectionsReportPdf({ projectName, sections, companyLogoUrl = null }) {
  const logo = companyLogoUrl ? await loadLogoDataUrlForPdf(companyLogoUrl) : null;

  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageH = PT_H;
  let y = MARGIN;

  const addPage = () => { pdf.addPage(); y = MARGIN; };
  const addPageIfNeeded = (needPt) => { if (y + needPt > pageH - BOTTOM) addPage(); };

  const writeLines = (text, size = 10, bold = false) => {
    if (!text) return;
    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(text, CONTENT_W);
    for (const line of lines) { addPageIfNeeded(14); pdf.text(line, MARGIN, y); y += size + 2; }
  };

  const gen = new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

  // ── Cover page (page 1) ──────────────────────────────────────────────────────
  if (logo) {
    const logoX = 595 - MARGIN - logo.wPt;
    try { pdf.addImage(logo.dataUrl, logo.format, logoX, 20, logo.wPt, logo.hPt, undefined, 'FAST'); } catch { /* */ }
  }

  let coverY = logo ? Math.max(140, 28 + logo.hPt + 20) : 140;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(30);
  pdf.setTextColor(30, 41, 59);
  const coverPnLines = pdf.splitTextToSize(projectName || 'Project', CONTENT_W);
  for (const line of coverPnLines) { pdf.text(line, MARGIN, coverY); coverY += 36; }

  coverY += 8;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(15);
  pdf.setTextColor(100, 116, 139);
  pdf.text('Snags & Variations Report', MARGIN, coverY);
  coverY += 28;

  pdf.setFontSize(9);
  pdf.setTextColor(148, 163, 184);
  pdf.text(`Generated: ${gen}`, MARGIN, coverY);
  coverY += 40;

  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.5);
  pdf.line(MARGIN, coverY, MARGIN + CONTENT_W, coverY);
  coverY += 20;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(148, 163, 184);
  pdf.text('CONTENTS', MARGIN, coverY);
  coverY += 14;

  for (const [i, sec] of sections.entries()) {
    const secItems = sec.items || [];
    const nSnags = secItems.filter(it => it.category !== 'variation').length;
    const nVars = secItems.filter(it => it.category === 'variation').length;
    let secCountLabel;
    if (nSnags > 0 && nVars > 0) secCountLabel = `${nSnags}s · ${nVars}v`;
    else if (nVars > 0) secCountLabel = `${nVars} variation${nVars !== 1 ? 's' : ''}`;
    else secCountLabel = `${nSnags} snag${nSnags !== 1 ? 's' : ''}`;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(30, 41, 59);
    pdf.text(`${i + 1}.  ${sec.name}`, MARGIN + 4, coverY);
    pdf.setFontSize(9);
    pdf.setTextColor(148, 163, 184);
    pdf.text(secCountLabel, MARGIN + CONTENT_W, coverY, { align: 'right' });
    coverY += 16;
    pdf.setDrawColor(241, 245, 249);
    pdf.setLineWidth(0.3);
    pdf.line(MARGIN + 4, coverY - 2, MARGIN + CONTENT_W, coverY - 2);
  }

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(148, 163, 184);
  pdf.text('Generated by CrewChain', MARGIN, PT_H - 26);

  // ── Content pages (page 2+) ──────────────────────────────────────────────────
  pdf.addPage();
  y = MARGIN;

  for (const [sIdx, section] of sections.entries()) {
    addPageIfNeeded(60);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(30, 41, 59);
    const secLines = pdf.splitTextToSize(`Section ${sIdx + 1}: ${section.name}`, CONTENT_W);
    for (const line of secLines) { pdf.text(line, MARGIN, y); y += 17; }
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(10);

    if (section.annotation) {
      const annLines = pdf.splitTextToSize(section.annotation, CONTENT_W - 26);
      const boxH = annLines.length * 13 + 18;
      addPageIfNeeded(boxH + 8);
      pdf.setFillColor(244, 246, 250);
      pdf.rect(MARGIN, y, CONTENT_W, boxH, 'F');
      pdf.setFillColor(30, 58, 95);
      pdf.rect(MARGIN, y, 3, boxH, 'F');
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(10);
      pdf.setTextColor(51, 65, 85);
      y += 11;
      for (const line of annLines) { pdf.text(line, MARGIN + 14, y); y += 13; }
      y += 7;
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      y += 20;
    } else {
      y += 8;
    }

    let snagIndex = 0;
    for (const it of section.items || []) {
      snagIndex += 1;
      addPageIfNeeded(100);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12);
      const itemTypeLabel = it.category === 'variation' ? 'Variation' : 'Snag';
      pdf.text(`${itemTypeLabel} ${snagIndex}`, MARGIN, y); y += 16;
      pdf.setFontSize(13);
      const snagTitleLines = pdf.splitTextToSize(it.title || 'Untitled', CONTENT_W);
      for (const line of snagTitleLines) { addPageIfNeeded(16); pdf.text(line, MARGIN, y); y += 15; }
      y += 4;

      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      const badges = [it.statusLabel, it.priorityLabel, it.categoryLabel].filter(Boolean).join('  ·  ');
      if (badges) {
        const bl = pdf.splitTextToSize(badges, CONTENT_W);
        for (const line of bl) { addPageIfNeeded(13); pdf.text(line, MARGIN, y); y += 12; }
      }
      if (it.createdLine) writeLines(it.createdLine, 10, false);
      if (it.assignedLine) { pdf.setTextColor(60, 60, 60); writeLines(it.assignedLine, 9, false); pdf.setTextColor(0, 0, 0); }
      if (it.resolvedLine) { pdf.setTextColor(16, 185, 129); writeLines(it.resolvedLine, 9, false); pdf.setTextColor(0, 0, 0); }
      y += 4;
      if (it.description) writeLines(it.description, 10, false);
      if (it.snagVoiceLine) {
        pdf.setFontSize(9);
        const vl = pdf.splitTextToSize(it.snagVoiceLine, CONTENT_W);
        for (const line of vl) { addPageIfNeeded(11); pdf.text(line, MARGIN, y); y += 10; }
        pdf.setFontSize(10);
      }

      // 2-up image grid
      const urls = it.imageUrls || [];
      if (urls.length > 0) addPageIfNeeded(160);
      for (let imgIdx = 0; imgIdx < urls.length; imgIdx += 2) {
        const isDouble = imgIdx + 1 < urls.length;
        if (isDouble) {
          const [raw1, raw2] = await Promise.all([
            loadImageForPdf(urls[imgIdx]),
            loadImageForPdf(urls[imgIdx + 1]),
          ]);
          const img1 = raw1 ? fitImageDrawSize(raw1, GRID_IMG_BOX) : null;
          const img2 = raw2 ? fitImageDrawSize(raw2, GRID_IMG_BOX) : null;
          const rowH = Math.max(img1?.hPt ?? 0, img2?.hPt ?? 0);
          if (rowH > 0) {
            if (y + rowH + 12 > pageH - BOTTOM) addPage();
            if (img1) { try { pdf.addImage(img1.dataUrl, 'JPEG', MARGIN, y, img1.wPt, img1.hPt, undefined, 'FAST'); } catch { /* */ } }
            if (img2) { try { pdf.addImage(img2.dataUrl, 'JPEG', MARGIN + GRID_COL_W + 8, y, img2.wPt, img2.hPt, undefined, 'FAST'); } catch { /* */ } }
            y += rowH + 8;
          } else {
            addPageIfNeeded(12);
            pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
            pdf.text('[Images could not be embedded]', MARGIN, y);
            pdf.setTextColor(0, 0, 0); pdf.setFontSize(10); y += 12;
          }
        } else {
          const raw = await loadImageForPdf(urls[imgIdx]);
          const loaded = raw ? fitImageDrawSize(raw, PDF_PRIMARY_PHOTO_BOX) : null;
          if (!loaded) {
            addPageIfNeeded(12);
            pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
            pdf.text('[Image could not be embedded]', MARGIN, y);
            pdf.setTextColor(0, 0, 0); pdf.setFontSize(10); y += 12;
            continue;
          }
          if (y + loaded.hPt + 12 > pageH - BOTTOM) addPage();
          try {
            pdf.addImage(loaded.dataUrl, 'JPEG', MARGIN, y, loaded.wPt, loaded.hPt, undefined, 'FAST');
            y += loaded.hPt + 8;
          } catch {
            addPageIfNeeded(12);
            pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
            pdf.text('[Image could not be embedded]', MARGIN, y);
            pdf.setTextColor(0, 0, 0); pdf.setFontSize(10); y += 12;
          }
        }
      }

      if (it.replies && it.replies.length > 0) {
        addPageIfNeeded(28);
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
        pdf.text('Replies (site)', MARGIN, y); y += 14;
        for (const rep of it.replies) {
          const label = rep.name ? `Reply — ${rep.name} · ${rep.timeLabel}` : `Reply · ${rep.timeLabel}`;
          pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
          const labLines = pdf.splitTextToSize(label, CONTENT_W);
          for (const line of labLines) { addPageIfNeeded(12); pdf.text(line, MARGIN, y); y += 11; }
          pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
          if (rep.text) {
            const tl = pdf.splitTextToSize(String(rep.text), CONTENT_W);
            for (const line of tl) { addPageIfNeeded(12); pdf.text(line, MARGIN, y); y += 11; }
          }
          if (rep.voiceLine) {
            pdf.setFontSize(8);
            const vl2 = pdf.splitTextToSize(rep.voiceLine, CONTENT_W);
            for (const line of vl2) { addPageIfNeeded(10); pdf.text(line, MARGIN, y); y += 10; }
            pdf.setFontSize(10);
          }
          for (const cu of rep.imageUrls || []) {
            const raw = await loadImageForPdf(cu);
            const cImg = raw ? fitImageDrawSize(raw, PDF_INLINE_PHOTO_BOX) : null;
            if (cImg && cImg.wPt > 0) {
              if (y + cImg.hPt > pageH - BOTTOM) addPage();
              try { pdf.addImage(cImg.dataUrl, 'JPEG', MARGIN, y, cImg.wPt, cImg.hPt); y += cImg.hPt + 6; } catch { /* */ }
            }
          }
          for (const pm of rep.pmNotes || []) {
            y += 4;
            const pmLabel = pm.name ? `PM note — ${pm.name} · ${pm.timeLabel}` : `PM note · ${pm.timeLabel}`;
            pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
            const pmLab = pdf.splitTextToSize(pmLabel, CONTENT_W - INDENT);
            for (const line of pmLab) { addPageIfNeeded(12); pdf.text(line, MARGIN + INDENT, y); y += 11; }
            pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
            if (pm.text) {
              const pml = pdf.splitTextToSize(String(pm.text), CONTENT_W - INDENT);
              for (const line of pml) { addPageIfNeeded(12); pdf.text(line, MARGIN + INDENT, y); y += 11; }
            }
            if (pm.voiceLine) {
              pdf.setFontSize(8);
              const pvl = pdf.splitTextToSize(pm.voiceLine, CONTENT_W - INDENT);
              for (const line of pvl) { addPageIfNeeded(10); pdf.text(line, MARGIN + INDENT, y); y += 10; }
              pdf.setFontSize(10);
            }
            for (const pu of pm.imageUrls || []) {
              const raw = await loadImageForPdf(pu);
              const pImg = raw ? fitImageDrawSize(raw, PDF_PM_NOTE_PHOTO_BOX) : null;
              if (pImg && pImg.wPt > 0) {
                if (y + pImg.hPt > pageH - BOTTOM) addPage();
                try { pdf.addImage(pImg.dataUrl, 'JPEG', MARGIN + INDENT, y, pImg.wPt, pImg.hPt); y += pImg.hPt + 6; } catch { /* */ }
              }
            }
          }
          y += 6;
        }
      }

      // Snag divider (lighter within section)
      y += 6;
      addPageIfNeeded(16);
      pdf.setDrawColor(200, 210, 220);
      pdf.setLineWidth(0.4);
      pdf.line(MARGIN + 20, y, MARGIN + CONTENT_W - 20, y);
      pdf.setLineWidth(0.2);
      y += 12;
    }

    // Section divider
    y += 6;
    addPageIfNeeded(24);
    pdf.setDrawColor(30, 58, 95);
    pdf.setLineWidth(1);
    pdf.line(MARGIN, y, MARGIN + CONTENT_W, y);
    pdf.setLineWidth(0.2);
    y += 20;
  }

  // Footer on content pages only (skip cover = page 1); renumber from 1
  stampFooter(pdf, pdf.getNumberOfPages(), projectName, { startPage: 2, pageOffset: 1 });

  const safe = (projectName || 'project').replace(/[^a-z0-9]/gi, '-');
  const d = new Date();
  const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  pdf.save(`snags-report-${safe}-${stamp}.pdf`);
}

export { VOICE_PDF };
